/**
 * Wave AL — Case Activity Log + full matter timeline.
 *
 * Standing React rule: every sub-component here is declared at module scope
 * (never inside CaseActivityPanel), so typing in the dialog never remounts it.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Edit as EditIcon,
  DeleteOutline,
  Gavel,
  Phone,
  Description,
  Event as EventIcon,
  StickyNote2,
  History,
  Lock,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';
import {
  ASSIGNABLE_USERS_QUERY_KEY,
  asAssignableUsers,
  fetchAssignableUsers,
} from '../../lib/assignableUsers';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../../utils/datetimeLocal';
import { useAuth } from '../../contexts/AuthContext';

type Group = 'court' | 'client' | 'external' | 'litigation' | 'internal';

const TYPES: { key: string; label: string; group: Group }[] = [
  { key: 'filing', label: 'Filing', group: 'court' },
  { key: 'hearing', label: 'Hearing', group: 'court' },
  { key: 'court_appearance', label: 'Court appearance', group: 'court' },
  { key: 'client_call', label: 'Client call', group: 'client' },
  { key: 'client_email', label: 'Client email', group: 'client' },
  { key: 'client_meeting', label: 'Client meeting', group: 'client' },
  { key: 'client_letter', label: 'Client letter', group: 'client' },
  { key: 'client_text', label: 'Client message / text', group: 'client' },
  { key: 'opposing_counsel', label: 'Opposing counsel contact', group: 'external' },
  { key: 'third_party', label: 'Third-party contact', group: 'external' },
  { key: 'deposition', label: 'Deposition', group: 'litigation' },
  { key: 'discovery', label: 'Discovery', group: 'litigation' },
  { key: 'negotiation', label: 'Negotiation / settlement', group: 'litigation' },
  { key: 'research', label: 'Legal research', group: 'internal' },
  { key: 'drafting', label: 'Drafting', group: 'internal' },
  { key: 'internal', label: 'Internal / file review', group: 'internal' },
  { key: 'other', label: 'Other', group: 'internal' },
];
const TYPE_BY_KEY = Object.fromEntries(TYPES.map((t) => [t.key, t]));
const GROUP_LABEL: Record<Group, string> = {
  court: 'Court',
  client: 'Client communication',
  external: 'Opposing counsel / third parties',
  litigation: 'Litigation',
  internal: 'Internal work',
};
const GROUP_COLOR: Record<Group, 'primary' | 'success' | 'warning' | 'secondary' | 'default'> = {
  court: 'primary',
  client: 'success',
  external: 'warning',
  litigation: 'secondary',
  internal: 'default',
};
const COURT_TYPES = new Set(['filing', 'hearing', 'court_appearance', 'deposition']);
const HEARING_TYPES = new Set(['hearing', 'court_appearance', 'deposition']);
const CONTACT_TYPES = new Set(['client_call', 'client_email', 'client_meeting', 'client_letter', 'client_text', 'opposing_counsel', 'third_party']);

interface ActivityForm {
  activityType: string;
  subject: string;
  details: string;
  occurredAt: string; // datetime-local
  durationMinutes: string;
  performedBy: string;
  court: string;
  department: string;
  judge: string;
  outcome: string;
  referenceNumber: string;
  nextDate: string; // datetime-local
  contactName: string;
  direction: string;
  isPrivileged: boolean;
  isConfidential: boolean;
  createTimeEntry: boolean;
  scheduleNextDate: boolean;
}

const emptyForm = (userId?: string): ActivityForm => ({
  activityType: 'client_call',
  subject: '',
  details: '',
  occurredAt: toDatetimeLocalValue(new Date()),
  durationMinutes: '',
  performedBy: userId || '',
  court: '',
  department: '',
  judge: '',
  outcome: '',
  referenceNumber: '',
  nextDate: '',
  contactName: '',
  direction: 'outbound',
  isPrivileged: false,
  isConfidential: false,
  createTimeEntry: false,
  scheduleNextDate: true,
});

const formFromActivity = (a: any): ActivityForm => ({
  ...emptyForm(),
  activityType: a.activityType,
  subject: a.subject || '',
  details: a.details || '',
  occurredAt: toDatetimeLocalValue(a.occurredAt),
  durationMinutes: a.durationMinutes != null ? String(a.durationMinutes) : '',
  performedBy: a.performedBy || '',
  court: a.court || '',
  department: a.department || '',
  judge: a.judge || '',
  outcome: a.outcome || '',
  referenceNumber: a.referenceNumber || '',
  nextDate: toDatetimeLocalValue(a.nextDate),
  contactName: a.contactName || '',
  direction: a.direction || 'outbound',
  isPrivileged: Boolean(a.isPrivileged),
  isConfidential: Boolean(a.isConfidential),
  createTimeEntry: false,
  scheduleNextDate: !a.calendarEventId,
});

const personName = (u: any) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : '');
const fmt = (v?: string | null, pattern = 'MMM d, yyyy h:mm a') => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

async function api(url: string, init?: RequestInit, fallback = 'Request failed') {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res.json();
}

// ---------------------------------------------------------------------------
// Module-scope sub-components
// ---------------------------------------------------------------------------

interface ActivityCardProps {
  activity: any;
  canEdit: boolean;
  onEdit: (a: any) => void;
  onDelete: (a: any) => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity: a, canEdit, onEdit, onDelete }) => {
  const t = TYPE_BY_KEY[a.activityType] || { label: a.activityType, group: 'internal' as Group };
  const edits = Array.isArray(a.editHistory) ? a.editHistory.length : 0;
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Chip size="small" label={t.label} color={GROUP_COLOR[t.group]} variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                {fmt(a.occurredAt)}
                {a.durationMinutes ? ` · ${a.durationMinutes} min` : ''}
                {a.performer ? ` · ${personName(a.performer)}` : ''}
              </Typography>
              {a.isPrivileged && <Chip size="small" icon={<Lock />} label="Privileged" color="error" variant="outlined" />}
              {a.isConfidential && <Chip size="small" label="Confidential" variant="outlined" />}
              {a.timeEntryId && <Chip size="small" label="Billed" color="success" variant="outlined" />}
            </Stack>
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>{a.subject}</Typography>
            {(a.court || a.department || a.judge || a.referenceNumber) && (
              <Typography variant="body2" color="text.secondary">
                {[a.court, a.department && `Dept. ${a.department}`, a.judge && `Judge ${a.judge}`, a.referenceNumber && `Ref. ${a.referenceNumber}`]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
            {a.contactName && (
              <Typography variant="body2" color="text.secondary">
                {a.direction === 'inbound' ? 'From' : 'With'}: {a.contactName}
              </Typography>
            )}
            {a.details && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{a.details}</Typography>
            )}
            {a.outcome && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                <strong>Outcome:</strong> {a.outcome}
              </Typography>
            )}
            {a.nextDate && (
              <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                Next date: {fmt(a.nextDate)} {a.calendarEventId ? '(on calendar)' : ''}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {edits > 0 && (
              <Tooltip
                title={`Edited ${edits} time${edits === 1 ? '' : 's'} — last ${fmt(a.editHistory[edits - 1]?.at)}. Prior values are retained.`}
              >
                <History fontSize="small" color="action" />
              </Tooltip>
            )}
            {canEdit && (
              <>
                <IconButton size="small" aria-label="Edit activity" onClick={() => onEdit(a)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Remove activity" onClick={() => onDelete(a)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

const KIND_ICON: Record<string, React.ReactElement> = {
  activity: <Gavel fontSize="small" />,
  note: <StickyNote2 fontSize="small" />,
  call: <Phone fontSize="small" />,
  document: <Description fontSize="small" />,
  event: <EventIcon fontSize="small" />,
};
const KIND_LABEL: Record<string, string> = {
  activity: 'Activity',
  note: 'Note',
  call: 'Call',
  document: 'Document',
  event: 'Calendar',
};

const TimelineRow: React.FC<{ item: any }> = ({ item }) => {
  const typeLabel = item.kind === 'activity' ? TYPE_BY_KEY[item.type]?.label : item.type?.replace?.(/_/g, ' ');
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ color: 'text.secondary', pt: 0.25 }}>{KIND_ICON[item.kind]}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">{fmt(item.at)}</Typography>
          <Chip size="small" label={KIND_LABEL[item.kind]} variant="outlined" />
          {typeLabel && <Typography variant="caption" color="text.secondary">{typeLabel}</Typography>}
          {item.by && <Typography variant="caption" color="text.secondary">· {item.by}</Typography>}
          {item.privileged && <Chip size="small" label="Privileged" color="error" variant="outlined" />}
          {item.confidential && <Chip size="small" label="Confidential" variant="outlined" />}
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{item.title}</Typography>
        {item.summary && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {item.summary}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

interface ActivityDialogProps {
  open: boolean;
  editing: any | null;
  form: ActivityForm;
  setForm: React.Dispatch<React.SetStateAction<ActivityForm>>;
  staff: any[];
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}

const ActivityDialog: React.FC<ActivityDialogProps> = ({ open, editing, form, setForm, staff, saving, onClose, onSave }) => {
  const set = (k: keyof ActivityForm) => (e: any) =>
    setForm((f) => ({ ...f, [k]: e?.target?.type === 'checkbox' ? e.target.checked : e.target.value }));
  const needsDuration = form.createTimeEntry && !editing?.timeEntryId;
  const isCourt = COURT_TYPES.has(form.activityType);
  const isHearing = HEARING_TYPES.has(form.activityType);
  const isContact = CONTACT_TYPES.has(form.activityType);
  const groups: Group[] = ['court', 'client', 'external', 'litigation', 'internal'];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{editing ? 'Edit activity' : 'Log activity'}</DialogTitle>
      <DialogContent>
        {editing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Changes are recorded — the previous values stay in this activity&apos;s edit history.
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="al-type">Activity type</InputLabel>
              <Select labelId="al-type" label="Activity type" value={form.activityType} onChange={set('activityType')}>
                {groups.flatMap((g) => [
                  <ListSubheader key={`h-${g}`}>{GROUP_LABEL[g]}</ListSubheader>,
                  ...TYPES.filter((t) => t.group === g).map((t) => (
                    <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="When it happened"
              type="datetime-local"
              fullWidth
              value={form.occurredAt}
              onChange={set('occurredAt')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Subject"
              required
              fullWidth
              value={form.subject}
              onChange={set('subject')}
              inputProps={{ maxLength: 255 }}
              placeholder={isCourt ? 'e.g. Filed Petition for Dissolution (FL-100)' : 'e.g. Discussed settlement offer with client'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="al-by">Performed by</InputLabel>
              <Select labelId="al-by" label="Performed by" value={form.performedBy} onChange={set('performedBy')}>
                {staff.map((u: any) => (
                  <MenuItem key={u.id} value={u.id}>{personName(u)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Duration (minutes)"
              type="number"
              fullWidth
              value={form.durationMinutes}
              onChange={set('durationMinutes')}
              inputProps={{ min: 0, max: 1440 }}
              required={needsDuration}
              error={needsDuration && !Number(form.durationMinutes)}
              helperText={needsDuration && !Number(form.durationMinutes) ? 'Enter the minutes to bill' : ' '}
            />
          </Grid>

          {isContact && (
            <>
              <Grid item xs={12} sm={8}>
                <TextField label="Contact name" fullWidth value={form.contactName} onChange={set('contactName')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="al-dir">Direction</InputLabel>
                  <Select labelId="al-dir" label="Direction" value={form.direction} onChange={set('direction')}>
                    <MenuItem value="outbound">Outbound</MenuItem>
                    <MenuItem value="inbound">Inbound</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {isCourt && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField label="Court" fullWidth value={form.court} onChange={set('court')} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Dept. / courtroom" fullWidth value={form.department} onChange={set('department')} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label={form.activityType === 'filing' ? 'Filing / envelope #' : 'Reference #'}
                  fullWidth
                  value={form.referenceNumber}
                  onChange={set('referenceNumber')}
                />
              </Grid>
              {isHearing && (
                <Grid item xs={12} sm={6}>
                  <TextField label="Judge / officer" fullWidth value={form.judge} onChange={set('judge')} />
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <TextField
              label={isHearing ? 'Hearing notes' : 'Details'}
              fullWidth
              multiline
              minRows={3}
              value={form.details}
              onChange={set('details')}
            />
          </Grid>

          {(isCourt || form.activityType === 'negotiation') && (
            <>
              <Grid item xs={12}>
                <TextField label="Outcome / ruling" fullWidth multiline minRows={2} value={form.outcome} onChange={set('outcome')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Next date (continuance / follow-up)"
                  type="datetime-local"
                  fullWidth
                  value={form.nextDate}
                  onChange={set('nextDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                {editing?.calendarEventId ? (
                  <Chip color="success" variant="outlined" label="Next date is on the firm calendar" />
                ) : (
                  <FormControlLabel
                    control={<Checkbox checked={form.scheduleNextDate} onChange={set('scheduleNextDate')} disabled={!form.nextDate} />}
                    label={form.nextDate ? 'Add next date to the firm calendar' : 'Add next date to the firm calendar (set a next date first)'}
                  />
                )}
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Checkbox checked={form.isPrivileged} onChange={set('isPrivileged')} />}
                label="Attorney-client privileged"
              />
              <FormControlLabel
                control={<Checkbox checked={form.isConfidential} onChange={set('isConfidential')} />}
                label="Confidential"
              />
              {editing?.timeEntryId ? (
                <Chip color="success" variant="outlined" label="Billed — time entry created" sx={{ alignSelf: 'center' }} />
              ) : (
                <FormControlLabel
                  control={<Checkbox checked={form.createTimeEntry} onChange={set('createTimeEntry')} />}
                  label="Create billable time entry"
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !form.subject.trim() || (needsDuration && !Number(form.durationMinutes))}
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Log activity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------

interface CaseActivityPanelProps {
  caseId: string;
}

const CaseActivityPanel: React.FC<CaseActivityPanelProps> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth() as any;
  const [view, setView] = useState<'log' | 'timeline'>('log');
  const [group, setGroup] = useState<'all' | Group>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState<ActivityForm>(emptyForm(user?.id));

  // UI hint only — the server enforces author-or-firm-admin on PUT/DELETE.
  const isAdmin = Boolean(
    user &&
      (user.role === 'admin' ||
        (user.role === 'lawyer' && user.isManagingAttorney) ||
        (user.role === 'paralegal' && user.isOfficeManager))
  );

  const { data: staffData } = useQuery({
    queryKey: ASSIGNABLE_USERS_QUERY_KEY,
    queryFn: fetchAssignableUsers,
    staleTime: 5 * 60 * 1000,
  });
  const staff = asAssignableUsers(staffData);

  const listKey = ['case-activities', caseId, group];
  const { data: listData, isLoading, error } = useQuery({
    queryKey: listKey,
    enabled: Boolean(caseId) && view === 'log',
    queryFn: () => {
      const params = new URLSearchParams({ caseId, limit: '200' });
      if (group !== 'all') params.set('group', group);
      return api(`/api/case-activities?${params}`, undefined, 'Unable to load activities');
    },
  });
  const activities: any[] = listData?.data?.activities || [];
  const total: number = listData?.data?.pagination?.total ?? activities.length;

  const { data: tlData, isLoading: tlLoading, error: tlError } = useQuery({
    queryKey: ['case-timeline', caseId],
    enabled: Boolean(caseId) && view === 'timeline',
    queryFn: () => api(`/api/case-activities/timeline?caseId=${caseId}&limit=300`, undefined, 'Unable to load timeline'),
  });
  const timeline: any[] = tlData?.data?.items || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['case-activities', caseId] });
    queryClient.invalidateQueries({ queryKey: ['case-timeline', caseId] });
    queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        activityType: form.activityType,
        subject: form.subject.trim(),
        details: form.details || null,
        occurredAt: fromDatetimeLocalValue(form.occurredAt) || new Date().toISOString(),
        durationMinutes: form.durationMinutes === '' ? null : Number(form.durationMinutes),
        performedBy: form.performedBy || null,
        court: form.court || null,
        department: form.department || null,
        judge: form.judge || null,
        outcome: form.outcome || null,
        referenceNumber: form.referenceNumber || null,
        nextDate: fromDatetimeLocalValue(form.nextDate),
        contactName: form.contactName || null,
        direction: CONTACT_TYPES.has(form.activityType) ? form.direction : null,
        isPrivileged: form.isPrivileged,
        isConfidential: form.isConfidential,
        scheduleNextDate: Boolean(form.nextDate) && form.scheduleNextDate,
      };
      if (editing) {
        return api(
          `/api/case-activities/${editing.id}`,
          { method: 'PUT', body: JSON.stringify({ ...payload, createTimeEntry: form.createTimeEntry && !editing.timeEntryId }) },
          'Failed to update activity'
        );
      }
      return api(
        '/api/case-activities',
        { method: 'POST', body: JSON.stringify({ ...payload, caseId, createTimeEntry: form.createTimeEntry }) },
        'Failed to log activity'
      );
    },
    onSuccess: () => {
      toast.success(editing ? 'Activity updated' : 'Activity logged');
      invalidate();
      setDialogOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Failed to save activity')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/case-activities/${id}`, { method: 'DELETE' }, 'Failed to remove activity'),
    onSuccess: () => {
      toast.success('Activity removed');
      invalidate();
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Failed to remove activity')),
  });

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(user?.id));
    setDialogOpen(true);
  };
  const openEdit = (a: any) => {
    setEditing(a);
    setForm(formFromActivity(a));
    setDialogOpen(true);
  };
  const confirmDelete = (a: any) => {
    // Inline confirm via toast would be nicer; window.confirm keeps it explicit and dependency-free.
    if (window.confirm(`Remove "${a.subject}" from the activity log? It is retained in the audit trail.`)) {
      deleteMutation.mutate(a.id);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon fontSize="small" /> {view === 'log' ? `Activity log (${total})` : 'Matter timeline'}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
            <ToggleButton value="log">Activity log</ToggleButton>
            <ToggleButton value="timeline">Full timeline</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openNew}>
            Log activity
          </Button>
        </Stack>
      </Box>

      {view === 'log' && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {(['all', 'court', 'client', 'external', 'litigation', 'internal'] as const).map((g) => (
              <Chip
                key={g}
                label={g === 'all' ? 'All' : GROUP_LABEL[g]}
                color={group === g ? 'primary' : 'default'}
                variant={group === g ? 'filled' : 'outlined'}
                onClick={() => setGroup(g)}
              />
            ))}
          </Stack>
          {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
          {error && <Alert severity="error">{toUserErrorMessage(error, 'Unable to load activities')}</Alert>}
          {!isLoading && !error && activities.length === 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography color="text.secondary">
                  No activities logged{group === 'all' ? '' : ` in ${GROUP_LABEL[group as Group].toLowerCase()}`} yet. Use
                  Log activity to record filings, hearings, client communications and other work on this matter.
                </Typography>
              </CardContent>
            </Card>
          )}
          <Stack spacing={1}>
            {activities.map((a) => (
              <ActivityCard
                key={a.id}
                activity={a}
                canEdit={isAdmin || a.createdBy === user?.id}
                onEdit={openEdit}
                onDelete={confirmDelete}
              />
            ))}
          </Stack>
        </>
      )}

      {view === 'timeline' && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Activities, notes, calls, documents and calendar events on this matter, newest first.
          </Typography>
          {tlLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
          {tlError && <Alert severity="error">{toUserErrorMessage(tlError, 'Unable to load timeline')}</Alert>}
          {!tlLoading && !tlError && timeline.length === 0 && (
            <Typography color="text.secondary">Nothing recorded on this matter yet.</Typography>
          )}
          {timeline.map((item) => (
            <TimelineRow key={`${item.kind}-${item.id}`} item={item} />
          ))}
        </>
      )}

      <ActivityDialog
        open={dialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        staff={staff}
        saving={saveMutation.isPending}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSave={() => saveMutation.mutate()}
      />
    </Box>
  );
};

export default CaseActivityPanel;
